import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  FormControl,
  Select,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';

import { getAllTeams } from './functions/get_all_teams.fucntion';
import { deleteTeam } from './functions/delete_team.function';

export const TeamManagementTable = forwardRef(({ onEdit, onDeleteSuccess }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);

  // Define cities and regions
  const cities = useMemo(
    () => [
      'Karachi',
      'Lahore',
      'Islamabad',
      'Peshawar',
      'Quetta',
      'Multan',
      'Faisalabad',
      'Rawalpindi',
      'Gujranwala',
      'Sialkot',
      'Abbottabad',
      'Bahawalpur',
      'Sargodha',
      'Sahiwal',
      'Jhang',
      'Okara',
      'Gujrat',
      'Kasur',
      'Rahim Yar Khan',
      'Mardan',
      'Sheikhupura',
      'Chiniot',
      'Kamoke',
      'Mandi Bahauddin',
      'Hafizabad',
      'Sadiqabad',
      'Jhelum',
      'Gujar Khan',
      'Narowal',
      'Muridke',
      'Nowshera',
    ],
    []
  );

  const regions = useMemo(() => ['North', 'South', 'Central B', 'Central A'], []);

  // Function to fetch teams data
  const fetchTeams = async () => {
    setLoading(true);
    try {
      const result = await getAllTeams();
      const responseArray = result?.data || [];
      setTeams(responseArray);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Expose refetchTeams method to parent component
  useImperativeHandle(ref, () => ({
    refetchTeams: fetchTeams,
  }));

  const getInitials = (name) => {
    if (!name) {
      return '';
    }

    const parts = name.trim().split(' ');

    if (parts.length === 1) {
      // single word → first 2 letters
      return parts[0].substring(0, 2).toUpperCase();
    }

    // multiple words → first letter of first + last word
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchTeams();
  }, []);

  const filteredTeams = useMemo(() => {
    let filtered = teams;

    // Filter by city
    if (selectedCity) {
      filtered = filtered.filter((team) => team.city === selectedCity);
    }

    // Filter by region
    if (selectedRegion) {
      filtered = filtered.filter((team) => team.region === selectedRegion);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((team) =>
        Object.values(team).some((val) =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  }, [selectedCity, selectedRegion, searchTerm, teams]);

  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleDelete = async () => {
      try {
        setDeleting(true);
        const result = await deleteTeam(row.original.id);
        if (result.success) {
          await fetchTeams();
          if (onDeleteSuccess) {
            onDeleteSuccess();
          }
        }
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setDeleting(false);
        handleClose();
      }
    };

    return (
      <>
        <IconButton onClick={handleClick} disabled={deleting}>
          <MoreVertIcon sx={{ fontWeight: 300, fontSize: '18px', color: '#667085' }} />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              // eslint-disable-next-line react/prop-types
              onEdit(row.original);
              handleClose();
            }}
            disabled={deleting}
          >
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Team Name',
        size: 150,
        enableColumnActions: false,
      },
      {
        accessorKey: 'members',
        header: 'Members',
        size: 160,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const members = cell.getValue() || [];
          const displayedMembers = members.slice(0, 2); // show max 2
          const extraCount = members.length - displayedMembers.length;

          return (
            <div style={{ display: 'flex', gap: '2px' }}>
              {displayedMembers.map((m) => (
                <Avatar
                  key={m.id}
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '12px',
                    bgcolor: '#E8E9E9',
                    color: '#344054',
                  }}
                >
                  {getInitials(m.name)}
                </Avatar>
              ))}

              {extraCount > 0 && (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '12px',
                    fontWeight: 300,
                    bgcolor: 'transparent',
                    color: '#1D242E',
                  }}
                >
                  +{extraCount}
                </Avatar>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 130,
        enableColumnActions: false,
      },
      {
        accessorKey: 'region',
        header: 'Region',
        size: 130,
        enableColumnActions: false,
      },
      {
        accessorKey: 'created_by',
        header: 'Created By',
        size: 130,
        enableColumnActions: false,
      },
      {
        accessorKey: 'created_at',
        header: 'Created Date',
        size: 130,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const val = cell.getValue();
          if (!val) {
            return '';
          }
          const date = new Date(val);
          return date.toISOString().split('T')[0]; // YYYY-MM-DD
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created Time',
        size: 130,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const val = cell.getValue();
          if (!val) {
            return '';
          }
          const date = new Date(val);
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
        },
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <ActionsMenu row={row} />,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredTeams,
    state: { isLoading: loading },
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'none',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: true,
    enableSelectAll: true,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableSorting: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: {
      pagination: {
        pageSize: 10,
      },
      columnOrder: [
        'mrt-row-select',
        'name',
        'members',
        'city',
        'region',
        'created_by',
        'created_at',
        'created_at',
        'actions',
      ],
    },
    muiSelectCheckboxProps: {
      sx: {
        transform: 'scale(0.8)', // shrink size
        padding: 0, // remove extra padding
        margin: 0,
        color: 'gray', // unchecked color
        verticalAlign: 'middle',
        alignSelf: 'center',
        '&.Mui-checked': {
          color: '#BD1D23', // checked color
        },
      },
    },
    muiSelectAllCheckboxProps: {
      sx: {
        transform: 'scale(0.8)',
        color: 'gray',
        verticalAlign: 'middle',
        alignSelf: 'center',
        '&.Mui-checked': {
          color: '#BD1D23',
        },
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    // muiTableContainerProps: {},
    muiTableBodyRowProps: {
      sx: {
        height: 52, // consistent body row height
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        padding: '0 12px',

        '&:first-of-type': {
          paddingLeft: '12px !important',
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '56px', // matches row height
        padding: '0 12px', // override just in case
        alignItems: 'center',

        '&:first-of-type': {
          paddingLeft: '18px !important', // same as body rows
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: 56,
        '& .MuiTableCell-root': {
          padding: '6px 12px',
          verticalAlign: 'middle',
        },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side - Search field */}
        <TextField
          placeholder="Search"
          size="small"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          sx={{
            backgroundColor: '#fff',
            width: '300px',
            '& .MuiInputBase-input': {
              color: '#b3b9c4',
              '::placeholder': {
                color: '#b3b9c4',
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-root': {
              border: '1px solid #ccc',
              borderRadius: '10px',
              paddingRight: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Right side - City and Region dropdowns */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* City Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                CITIES
              </MenuItem>
              {cities.map((city, index) => (
                <MenuItem
                  key={city}
                  value={city}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== cities.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Region Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                REGIONS
              </MenuItem>
              {regions.map((region, index) => (
                <MenuItem
                  key={region}
                  value={region}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== regions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    ),
  });

  return (
    <div className="team-table-main-container">
      <div className="team-table-container">
        <div className="team-table-wrapper">
          <MaterialReactTable table={table} />
        </div>
      </div>
    </div>
  );
});

TeamManagementTable.displayName = 'TeamManagementTable';

TeamManagementTable.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDeleteSuccess: PropTypes.func,
};
